import React, { FocusEvent, KeyboardEvent, useRef, useState } from 'react';
import { ControlProps } from '../../api/models/control';

/**
 * Input Props
 */
type InputProps = ControlProps<string | number> & {
  /**
   * Html input type
   */
  type?: 'text' | 'number' | 'password' | 'tel' | 'search';
  /**
   * Size
   */
  size?: 'medium' | 'large';
  /**
   * Input mask
   */
  mask?: string;
  /**
   * Mask char
   */
  maskChar?: string;
  /**
   * Focus handler
   */
  onFocus?: (event: FocusEvent<HTMLInputElement>) => any;
  /**
   * Blur handler
   */
  onBlur?: (event: FocusEvent<HTMLInputElement>) => any;
  /**
   * Suggest
   */
  suggest?: boolean;
  /**
   * Suggestions list
   */
  suggestions?: string[] | { name: string }[];
  /**
   * Suggestion select handle
   */
  onSuggestionSelect?: (suggestion: string | { name: string }) => any;
  /**
   * Ref attr
   */
  api?: (ref: any) => any;
  /**
   * Key down handler
   */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => any;
  /**
   * Side handlers
   */
  on?: {
    change?: (value: string) => any;
  };
  /**
   * Placeholder
   */
  placeholder?: string;

  alwaysShowMask?: boolean;

  input?: React.MutableRefObject<HTMLInputElement>;

  refInput?: React.MutableRefObject<HTMLInputElement>;

  focused?: boolean;

  _onBlur?: () => void;

  _onFocus?: () => void;

  hasValue?: any;

  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

  onSuggestionClick?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    suggestion: string | { name: string }
  ) => void;

  isSuggestionsVisible?: boolean;

  values?: string | number;

  name?: string;

  phone?: boolean;

  googleAutoComplete?: boolean;

  geometryBounds?: any;

  onPlaceSelected?: (plase: any) => void;

  maxLength?: number;

  success?: boolean;
};

/**
 * Use Input
 */
const useInput = ({
  on,
  api,
  value,
  name,
  onFocus,
  onBlur,
  disabled,
  onChange,
  suggestions,
  onSuggestionSelect,
  ...props
}: InputProps) => {
  const [focused, setFocused] = useState(false);
  const [values, setValues] = useState(value);
  const hasValue = Boolean(value) || value == '0';
  const input = useRef<HTMLInputElement>();

  const isSuggestionsVisible =
    //@ts-ignore
    suggestions?.length > 0 && value?.toString()?.length > 2 && focused;

  const _onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);

    onFocus?.(event);
  };

  const _onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);

    onBlur?.(event);
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!focused) setFocused(true);

    const { value } = event.target;
    //@ts-ignore
    onChange(value);

    setValues(value);
    //@ts-ignore
    on?.change(value);
  };

  const onSuggestionClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    //@ts-ignore
    suggestion: (typeof suggestions)[number]
  ) => {
    event.preventDefault();
    //@ts-ignore
    input.current.blur();
    //@ts-ignore
    onChange(typeof suggestion == 'string' ? suggestion : suggestion.name);

    if (onSuggestionSelect) onSuggestionSelect(suggestion);
  };
  //@ts-ignore
  api({
    focused,
    focus: () => input?.current?.focus()
  });

  return {
    input,
    value,
    name,
    values,
    focused,
    _onBlur,
    _onFocus,
    hasValue,
    onInputChange,
    onSuggestionClick,
    isSuggestionsVisible
  };
};

export { useInput };
export type { InputProps };
